import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskedLine({ i, className, children }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span custom={i} variants={line} initial="hidden" animate="show" className={`block ${className}`}>
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1737381508529-a110d717d5a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt=""
          className="w-full h-[130%] object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 hero-vignette" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-4 sm:px-8 w-full pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="font-mono2 text-[10px] sm:text-xs tracking-[0.4em] text-[#E10600] uppercase mb-6 flex items-center gap-3"
          data-testid="hero-ibc-line"
        >
          <img src="/images/ibc-logo.webp" alt="IBC logo" className="h-6 w-6 object-contain" />
          Affiliated with IBC — Indian Boxing Council
        </motion.p>

        <h1 className="font-display leading-[0.88] tracking-tight text-[19vw] sm:text-[15vw] lg:text-[11rem] uppercase">
          <MaskedLine i={0} className="text-white">Masters</MaskedLine>
          <MaskedLine i={1} className="text-[#E10600]">Pro Boxing</MaskedLine>
        </h1>

        <div className="mt-8 sm:mt-10 max-w-xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="text-base sm:text-lg text-zinc-300 leading-relaxed"
            data-testid="hero-subtitle"
          >
            The official professional boxing organization for veteran fighters aged 40–55.
            <span className="text-white font-medium"> Experience never retires.</span>
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="font-mono2 text-[10px] sm:text-xs tracking-[0.35em] text-zinc-500 uppercase mt-5"
            data-testid="hero-founder-line"
          >
            Founder — <span className="text-[#E10600]">Manindar Singh</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/register"
              data-testid="hero-cta-register"
              className="group relative font-cond font-semibold tracking-[0.2em] text-sm sm:text-base bg-[#E10600] hover:bg-[#C00500] text-white px-8 sm:px-10 py-4 transition-colors duration-300"
            >
              STEP BACK THROUGH THE ROPES
              <span className="absolute inset-0 border border-[#E10600] translate-x-1.5 translate-y-1.5 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-300 -z-10" />
            </Link>
            <span className="font-mono2 text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Ages 40–55 · IBC Affiliated</span>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono2 text-[9px] tracking-[0.4em] text-zinc-600 uppercase"
      >
        Scroll
      </motion.div>
    </section>
  );
}
